export enum ToolbarGroups {
  Template = 'template',
  LoadFromTemplate = 'loadFromTemplate',
  History = 'history',
  Formatting = 'formatting',
  Alignment = 'alignment',
  Lists = 'lists',
  Media = 'media',
  Link = 'link',
  Table = 'table',
  Utility = 'utility',
  ClientUtility = 'clientUtility',
  TextStyle = 'textStyle',
  Colors = 'colors',
  Fonts = 'fonts',
  Indentation = 'indentation',
  RemoveFormat = 'removeFormat',
  Download = 'download',
}

export enum ToolbarButtons {
  Save = 'save',
  PageBreak = 'pagebreak',
  VisualChars = 'visualchars',
  VisualBlocks = 'visualblocks',
  CodeSample = 'codesample',
  InsertDateTime = 'insertdatetime',
  CharMap = 'charmap',
  Emoticons = 'emoticons',
  TemplateVariable = 'templateVariable',
  TemplateLink = 'templateLink',
  TemplateLoop = 'templateLoop',
  TemplateCondition = 'templateCondition',
  TemplateFunction = 'templateFunction',
  LoadFromTemplate = 'loadFromTemplate',
  Undo = 'undo',
  Redo = 'redo',
  Bold = 'bold',
  Italic = 'italic',
  Underline = 'underline',
  Strikethrough = 'strikethrough',
  Subscript = 'subscript',
  Superscript = 'superscript',
  AlignLeft = 'alignleft',
  AlignCenter = 'aligncenter',
  AlignRight = 'alignright',
  AlignJustify = 'alignjustify',
  BullList = 'bullist',
  NumList = 'numlist',
  Link = 'link',
  Image = 'image',
  Table = 'table',
  Code = 'code',
  Fullscreen = 'fullscreen',
  Preview = 'preview',
  Styles = 'styles',
  ForeColor = 'forecolor',
  BackColor = 'backcolor',
  FontFamily = 'fontfamily',
  FontSize = 'fontsize',
  Indent = 'indent',
  Outdent = 'outdent',
  RemoveFormat = 'removeformat',
  Media = 'media',
  ExportFile = 'exportFile',
}

export const TOOLBAR_GROUPS = {
  [ToolbarGroups.Template]: [
    ToolbarButtons.TemplateVariable,
    ToolbarButtons.TemplateLink,
    ToolbarButtons.TemplateLoop,
    ToolbarButtons.TemplateCondition,
    ToolbarButtons.TemplateFunction,
  ],
  [ToolbarGroups.LoadFromTemplate]: [ToolbarButtons.LoadFromTemplate],
  [ToolbarGroups.History]: [ToolbarButtons.Undo, ToolbarButtons.Redo],
  [ToolbarGroups.Formatting]: [
    ToolbarButtons.Bold,
    ToolbarButtons.Italic,
    ToolbarButtons.Underline,
    ToolbarButtons.Strikethrough,
  ],
  [ToolbarGroups.Alignment]: [
    ToolbarButtons.AlignLeft,
    ToolbarButtons.AlignCenter,
    ToolbarButtons.AlignRight,
    ToolbarButtons.AlignJustify,
  ],
  [ToolbarGroups.Lists]: [ToolbarButtons.BullList, ToolbarButtons.NumList],
  [ToolbarGroups.Media]: [ToolbarButtons.Image],
  [ToolbarGroups.Link]: [ToolbarButtons.Link],
  [ToolbarGroups.Table]: [ToolbarButtons.Table],
  [ToolbarGroups.Utility]: [
    ToolbarButtons.Code,
    ToolbarButtons.Fullscreen,
    ToolbarButtons.Preview,
  ],
  [ToolbarGroups.ClientUtility]: [
    ToolbarButtons.Fullscreen,
    ToolbarButtons.Preview,
  ],
  [ToolbarGroups.TextStyle]: [ToolbarButtons.Styles],
  [ToolbarGroups.Colors]: [ToolbarButtons.ForeColor, ToolbarButtons.BackColor],
  [ToolbarGroups.Fonts]: [ToolbarButtons.FontFamily, ToolbarButtons.FontSize],
  [ToolbarGroups.Indentation]: [ToolbarButtons.Indent, ToolbarButtons.Outdent],
  [ToolbarGroups.RemoveFormat]: [ToolbarButtons.RemoveFormat],
  [ToolbarGroups.Download]: [ToolbarButtons.ExportFile],
} as const;

export enum PluginNames {
  ContextMenu = 'contextmenu',
  Lists = 'lists',
  AdvList = 'advlist',
  Link = 'link',
  Autolink = 'autolink',
  Anchor = 'anchor',
  SearchReplace = 'searchreplace',
  Image = 'image',
  Table = 'table',
  Media = 'media',
  CharMap = 'charmap',
  Emoticons = 'emoticons',
  InsertDateTime = 'insertdatetime',
  Template = 'template',
  Code = 'code',
  CodeSample = 'codesample',
  Fullscreen = 'fullscreen',
  Preview = 'preview',
  VisualBlocks = 'visualblocks',
  VisualChars = 'visualchars',
  WordCount = 'wordcount',
  Directionality = 'directionality',
  NonBreaking = 'nonbreaking',
  PageBreak = 'pagebreak',
  Quickbars = 'quickbars',
  Save = 'save',
  Autoresize = 'autoresize',
  Autosave = 'autosave',
  ImportCss = 'importcss',
}

export interface IPluginDefinition {
  name: string;
  toolbarButtons?: string[];
  dependencies?: string[];
  category:
    | 'content'
    | 'formatting'
    | 'media'
    | 'utility'
    | 'custom'
    | 'template';
  defaultEnabled?: boolean;
}

export const PLUGIN_REGISTRY: Record<string, IPluginDefinition> = {
  // Content plugins
  lists: {
    name: PluginNames.Lists,
    toolbarButtons: [ToolbarButtons.BullList, ToolbarButtons.NumList],
    category: 'content',
    defaultEnabled: false,
  },
  advlist: {
    name: PluginNames.AdvList,
    dependencies: [PluginNames.Lists],
    category: 'content',
    defaultEnabled: false,
  },
  link: {
    name: PluginNames.Link,
    toolbarButtons: [ToolbarButtons.Link],
    category: 'content',
    defaultEnabled: false,
  },
  autolink: {
    name: PluginNames.Autolink,
    category: 'content',
    defaultEnabled: false,
  },
  anchor: {
    name: PluginNames.Anchor,
    category: 'content',
    defaultEnabled: false,
  },
  searchreplace: {
    name: PluginNames.SearchReplace,
    category: 'content',
    defaultEnabled: false,
  },

  // Media plugins
  image: {
    name: PluginNames.Image,
    toolbarButtons: [ToolbarButtons.Image],
    category: 'media',
    defaultEnabled: false,
  },
  table: {
    name: PluginNames.Table,
    toolbarButtons: [ToolbarButtons.Table],
    category: 'media',
    defaultEnabled: false,
  },
  media: {
    name: PluginNames.Media,
    toolbarButtons: [ToolbarButtons.Media],
    category: 'media',
    defaultEnabled: false,
  },

  // Formatting plugins
  charmap: {
    name: PluginNames.CharMap,
    toolbarButtons: [ToolbarButtons.CharMap],
    category: 'formatting',
    defaultEnabled: false,
  },
  emoticons: {
    name: PluginNames.Emoticons,
    toolbarButtons: [ToolbarButtons.Emoticons],
    category: 'formatting',
    defaultEnabled: false,
  },
  insertdatetime: {
    name: PluginNames.InsertDateTime,
    toolbarButtons: [ToolbarButtons.InsertDateTime],
    category: 'formatting',
    defaultEnabled: false,
  },
  template: {
    name: PluginNames.Template,
    category: 'formatting',
    defaultEnabled: false,
  },

  // Utility plugins
  code: {
    name: PluginNames.Code,
    toolbarButtons: [ToolbarButtons.Code],
    category: 'utility',
    defaultEnabled: false,
  },
  codesample: {
    name: PluginNames.CodeSample,
    toolbarButtons: [ToolbarButtons.CodeSample],
    category: 'utility',
    defaultEnabled: false,
  },
  fullscreen: {
    name: PluginNames.Fullscreen,
    toolbarButtons: [ToolbarButtons.Fullscreen],
    category: 'utility',
    defaultEnabled: false,
  },
  preview: {
    name: PluginNames.Preview,
    toolbarButtons: [ToolbarButtons.Preview],
    category: 'utility',
    defaultEnabled: false,
  },
  visualblocks: {
    name: PluginNames.VisualBlocks,
    toolbarButtons: [ToolbarButtons.VisualBlocks],
    category: 'utility',
    defaultEnabled: false,
  },
  visualchars: {
    name: PluginNames.VisualChars,
    toolbarButtons: [ToolbarButtons.VisualChars],
    category: 'utility',
    defaultEnabled: false,
  },
  wordcount: {
    name: PluginNames.WordCount,
    category: 'utility',
    defaultEnabled: false,
  },
  directionality: {
    name: PluginNames.Directionality,
    category: 'utility',
    defaultEnabled: false,
  },
  nonbreaking: {
    name: PluginNames.NonBreaking,
    category: 'utility',
    defaultEnabled: false,
  },
  pagebreak: {
    name: PluginNames.PageBreak,
    toolbarButtons: [ToolbarButtons.PageBreak],
    category: 'utility',
    defaultEnabled: false,
  },
  quickbars: {
    name: PluginNames.Quickbars,
    category: 'utility',
    defaultEnabled: false,
  },
  save: {
    name: PluginNames.Save,
    toolbarButtons: [ToolbarButtons.Save],
    category: 'utility',
    defaultEnabled: false,
  },
  autoresize: {
    name: PluginNames.Autoresize,
    category: 'utility',
    defaultEnabled: false,
  },
  autosave: {
    name: PluginNames.Autosave,
    category: 'utility',
    defaultEnabled: false,
  },
  importcss: {
    name: PluginNames.ImportCss,
    category: 'utility',
    defaultEnabled: false,
  },
};

export function getPlugin(name: string): IPluginDefinition | undefined {
  return PLUGIN_REGISTRY[name];
}

export function getPluginsByCategory(
  category: IPluginDefinition['category']
): IPluginDefinition[] {
  return Object.values(PLUGIN_REGISTRY).filter(
    (p: IPluginDefinition) => p.category === category
  );
}

export function validatePluginDependencies(pluginNames: string[]): {
  valid: boolean;
  missing: string[];
} {
  const enabled = new Set(pluginNames);
  const missing: string[] = [];

  pluginNames.forEach(name => {
    const plugin = PLUGIN_REGISTRY[name];
    if (plugin?.dependencies) {
      plugin.dependencies.forEach(dep => {
        if (!enabled.has(dep)) {
          missing.push(`${name} requires ${dep}`);
        }
      });
    }
  });

  return {
    valid: missing.length === 0,
    missing,
  };
}

export const FONT_SIZE_OPTIONS = [
  { value: '8pt', label: '8pt' },
  { value: '10pt', label: '10pt' },
  { value: '10.5pt', label: '10.5pt' },
  { value: '12pt', label: '12pt' },
  { value: '14pt', label: '14pt' },
  { value: '18pt', label: '18pt' },
  { value: '24pt', label: '24pt' },
  { value: '36pt', label: '36pt' },
];
